import { useTranslation } from 'react-i18next';
import { DropdownField, LoadingSpinner } from '@/components';
import { getCurrentDateTime } from '@/utils/getCurrentDateTime';
import { useColdHubPage } from '@/hooks/Dashboard/useColdHubPage';
import {
  DoughnutChartWithoutDetailAndLegend,
  OneLabelToBarsWithXChart,
} from '@/components/Charts';
import {
  DoughnutChartWithRoundLegendsCard,
  RoundedLegendTwoMetricChartCard,
  SingleMetricWithDetailAndIconCard,
} from '@/components/Cards';
import { Indicadores } from '@/types/dashboard/ColdHubPage';
import contractIcon from '../../../../../assets/icons/dashboard/mcn_icon_contrato.svg';
import styles from './ColdHubPage.module.scss';

type Legend = {
  color: string;
  text: string;
  metric: string;
  metricColor: string;
};

const legend1ForChartCard = {
  color: '#4780bf',
  text: 'MCN',
};

const legend2ForChartCard = {
  color: '#D3D3D3',
  text: 'TCP',
};

export const ColdHubPage = () => {
  const { t } = useTranslation('dashboardPage');
  const lastUpdate = getCurrentDateTime();
  const {
    clientOptions,
    currentCliente,
    loadingPageData,
    setCurrentCliente,
    coldHubPageData,
  } = useColdHubPage();

  const handleClienteChange = (e: {
    value: string | null;
    target?: { name: string };
  }) => {
    setCurrentCliente({
      value: e.value ?? '',
      name: e.target?.name ?? currentCliente.name,
    });
  };

  const transformIndicadoresToLegendDC = (indicadores: Indicadores): Legend => {
    return {
      color: '#1E90FF',
      text: t('coldHubPage.dias_consumidos'),
      metric: indicadores.diasConsumidos.toString(),
      metricColor: '#223466',
    };
  };

  const transformIndicadoresToLegendDR = (indicadores: Indicadores): Legend => {
    return {
      color: '#FFA500',
      text: t('coldHubPage.dias_restantes'),
      metric: indicadores.diasRestantes.toString(),
      metricColor: '#223466',
    };
  };

  if (loadingPageData)
    return (
      <div style={{ overflow: 'hidden' }} className={styles.dashboard}>
        <LoadingSpinner className={styles.spinner} />
      </div>
    );

  return (
    <>
      {' '}
      <div className={styles.pageContainer}>
        <div className={styles.pageHeader}>
          <div className={styles.filterAndUpdatedAtContainer}>
            <DropdownField
              id="cliente"
              name="cliente"
              options={clientOptions}
              value={currentCliente.value}
              label={t('coldHubPage.cliente')}
              className={styles.selectDropdown}
              placeholder={t('coldHubPage.seleccione_cliente')}
              onChange={handleClienteChange}
            />
            <p className={styles.lastUpdate}>
              Última actualización: <span>{lastUpdate}</span>
            </p>
          </div>
        </div>
      </div>
      <div className={styles.dashboard}>
        {' '}
        <div className={`${styles.column} ${styles.left}`}>
          <SingleMetricWithDetailAndIconCard
            icon={contractIcon}
            title={coldHubPageData?.indicadores.contrato ?? ''}
            subtitle={t('coldHubPage.dias_contrato')}
            metric={coldHubPageData?.indicadores.diasContrato.toString() ?? '0'}
            backgroundColor="#223466"
            textColor="#FFFFFF"
            metricColor="#FFA500"
          />

          <DoughnutChartWithRoundLegendsCard
            chartComponent={
              <DoughnutChartWithoutDetailAndLegend
                metric1={coldHubPageData?.indicadores.diasConsumidos ?? 0}
                metric2={coldHubPageData?.indicadores.diasRestantes ?? 0}
              />
            }
            legend1={transformIndicadoresToLegendDC(
              coldHubPageData?.indicadores ?? {
                diasConsumidos: 0,
                diasContrato: 0,
                diasRestantes: 0,
                contrato: '',
                inicio: '',
                fin: '',
              }
            )}
            legend2={transformIndicadoresToLegendDR(
              coldHubPageData?.indicadores ?? {
                diasConsumidos: 0,
                diasContrato: 0,
                diasRestantes: 0,
                contrato: '',
                inicio: '',
                fin: '',
              }
            )}
            backgroundColor="#FFFFFF"
            textColor="#223466"
          />
          <RoundedLegendTwoMetricChartCard
            title="Boxes"
            subtitle={t('coldHubPage.periodo_desde_ingreso_hasta_carga')}
            legend1={legend1ForChartCard}
            legend2={legend2ForChartCard}
            chartComponent={
              <OneLabelToBarsWithXChart
                data={coldHubPageData?.graficaBoxes ?? []}
              />
            }
            backgroundColor="#FFFFFF"
            textColor="#223466"
          />
        </div>
        <div className={`${styles.column} ${styles.center}`}>
          <RoundedLegendTwoMetricChartCard
            title={t('coldHubPage.dias_en_terminal')}
            subtitle={t('coldHubPage.periodo_desde_ingreso_hasta_carga')}
            legend1={legend1ForChartCard}
            legend2={legend2ForChartCard}
            chartComponent={
              <OneLabelToBarsWithXChart
                data={coldHubPageData?.graficaDiasTerminal ?? []}
              />
            }
            backgroundColor="#FFFFFF"
            textColor="#223466"
          />

          <RoundedLegendTwoMetricChartCard
            title={t('coldHubPage.dt_dias_en_terminal')}
            legend1={legend1ForChartCard}
            legend2={legend2ForChartCard}
            chartComponent={
              <OneLabelToBarsWithXChart
                data={coldHubPageData?.graficaDwellDiasTerminal ?? []}
              />
            }
            backgroundColor="#FFFFFF"
            textColor="#223466"
          />
        </div>
        <div className={`${styles.column} ${styles.right}`}>
          <RoundedLegendTwoMetricChartCard
            title={t('coldHubPage.dias_de_frio')}
            legend1={legend1ForChartCard}
            legend2={legend2ForChartCard}
            chartComponent={
              <OneLabelToBarsWithXChart
                data={coldHubPageData?.graficaDiasFrio ?? []}
              />
            }
            backgroundColor="#FFFFFF"
            textColor="#223466"
          />

          <RoundedLegendTwoMetricChartCard
            title={t('coldHubPage.dt_dias_de_frio')}
            legend1={legend1ForChartCard}
            legend2={legend2ForChartCard}
            chartComponent={
              <OneLabelToBarsWithXChart
                data={coldHubPageData?.graficaDwellTime ?? []}
              />
            }
            backgroundColor="#FFFFFF"
            textColor="#223466"
          />
        </div>
      </div>
    </>
  );
};
